export interface MindmapNode {
  id: string;
  label: string;
  children?: MindmapNode[];
}

export type ContentBlock =
  | { id: string; type: "text"; markdown: string }
  | { id: string; type: "code"; code: string; language?: string; filename?: string }
  | { id: string; type: "canvas2d"; code: string }
  | { id: string; type: "image"; url: string; alt?: string; caption?: string; isPlaceholder?: boolean }
  | { id: string; type: "slides"; slides: { imageUrl: string; caption?: string }[] }
  | { id: string; type: "flashcards"; cards: { term: string; definition: string }[] }
  | {
      id: string;
      type: "mindmap";
      centralLabel: string;
      nodes: MindmapNode[];
    }
  | {
      id: string;
      type: "video";
      youtubeVideoId?: string;
      videoUrl?: string;
      title: string;
      isIllustrative?: boolean;
    }
  | { id: string; type: "quiz"; questions: { question: string; answer: string }[] }
  | {
      id: string;
      type: "callout";
      variant: "warning" | "info" | "production";
      heading?: string;
      text: string;
    };

export type ContentBlockType = ContentBlock["type"];

export const BLOCK_TYPE_LABELS: Record<ContentBlockType, string> = {
  text: "Text",
  code: "Code",
  canvas2d: "Canvas 2D animation",
  image: "Image",
  slides: "Slides",
  flashcards: "Flashcards",
  mindmap: "Mind map",
  video: "Video",
  quiz: "Quiz",
  callout: "Callout",
};

export const BLOCK_TYPES: ContentBlockType[] = [
  "text",
  "code",
  "canvas2d",
  "image",
  "slides",
  "flashcards",
  "mindmap",
  "video",
  "quiz",
  "callout",
];

export const newId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `block-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export const createBlankBlock = (type: ContentBlockType): ContentBlock => {
  const id = newId();
  switch (type) {
    case "text":
      return { id, type, markdown: "" };
    case "code":
      return { id, type, code: "", language: "javascript" };
    case "canvas2d":
      return {
        id,
        type,
        code: "function draw(ctx, t, width, height) {\n  ctx.clearRect(0, 0, width, height);\n  ctx.fillStyle = '#4f7cff';\n  var x = width / 2 + Math.cos(t) * 120;\n  var y = height / 2 + Math.sin(t) * 120;\n  ctx.beginPath();\n  ctx.arc(x, y, 24, 0, Math.PI * 2);\n  ctx.fill();\n}",
      };
    case "image":
      return { id, type, url: "" };
    case "slides":
      return { id, type, slides: [] };
    case "flashcards":
      return { id, type, cards: [] };
    case "mindmap":
      return { id, type, centralLabel: "", nodes: [] };
    case "video":
      return { id, type, title: "" };
    case "quiz":
      return { id, type, questions: [] };
    case "callout":
      return { id, type, variant: "info", text: "" };
  }
};
